import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { fetchEpisode, fetchEpisodeBySlug, fetchPodcast, getPodcastColor, buildSpeakerBars, questionSplit, showsQuestionSplit, pageData } from '../api.js';
import { applyMeta, episodeMeta, slugify } from '../seo.js';
import SpeakingBar from '../components/SpeakingBar.jsx';
import EpisodeStats from '../components/EpisodeStats.jsx';
import QABalance from '../components/QABalance.jsx';


function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function formatDuration(ms) {
  if (ms == null) return '—';
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatRelativeTime(isoOrMs) {
  if (!isoOrMs) return '';
  const date = typeof isoOrMs === 'number' ? new Date(isoOrMs) : new Date(isoOrMs);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} hour${diffH === 1 ? '' : 's'} ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD} day${diffD === 1 ? '' : 's'} ago`;
  const diffMo = Math.floor(diffD / 30);
  return `${diffMo} month${diffMo === 1 ? '' : 's'} ago`;
}

function DetailSkeleton() {
  return (
    <div aria-hidden="true">
      <div className="skeleton" style={{ height: 14, width: '30%', marginBottom: '1.5rem' }} />
      <div className="skeleton" style={{ height: 28, width: '70%', marginBottom: '0.5rem' }} />
      <div className="skeleton" style={{ height: 12, width: '40%', marginBottom: '2rem' }} />
      <div className="skeleton" style={{ height: 180, width: '100%', borderRadius: 12 }} />
    </div>
  );
}

/* ── Star Rating ── */
function StarRating({ episodeUuid }) {
  const storageKey = `tm-rating-${episodeUuid}`;
  const [rating, setRating] = useState(() => {
    try { return parseInt(localStorage.getItem(storageKey) ?? '0', 10) || 0; } catch { return 0; }
  });
  const [hovered, setHovered] = useState(0);

  function handleRate(star) {
    setRating(star);
    try { localStorage.setItem(storageKey, String(star)); } catch {}
  }

  const display = hovered || rating;

  return (
    <div className="ep-rating">
      <p className="ep-section-label">Rate this episode</p>
      <div className="ep-rating__stars" onMouseLeave={() => setHovered(0)}>
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            className={`ep-rating__star${display >= star ? ' ep-rating__star--filled' : ''}`}
            onMouseEnter={() => setHovered(star)}
            onClick={() => handleRate(star)}
            aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
            aria-pressed={rating >= star}
          >
            {display >= star ? '★' : '☆'}
          </button>
        ))}
      </div>
      {rating > 0 && (
        <p className="ep-rating__feedback">
          {rating === 5 ? 'Legendary' : rating === 4 ? 'Great' : rating === 3 ? 'Good' : rating === 2 ? 'Meh' : 'Skip'}
        </p>
      )}
    </div>
  );
}

/* ── Comments ── */
function Comments({ episodeUuid, accentColor }) {
  const storageKey = `tm-comments-${episodeUuid}`;

  const loadComments = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  };

  const [comments, setComments] = useState(loadComments);
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    const trimName = name.trim();
    const trimText = text.trim();
    if (!trimName || !trimText) return;
    const newComment = { name: trimName, text: trimText, timestamp: Date.now() };
    const updated = [newComment, ...comments];
    setComments(updated);
    try { localStorage.setItem(storageKey, JSON.stringify(updated)); } catch {}
    setName('');
    setText('');
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 2500);
  }

  return (
    <div className="ep-comments">
      <p className="ep-section-label">
        Comments {comments.length > 0 && <span className="ep-comments__count">({comments.length})</span>}
      </p>

      {/* FE-012: proper label+input pairs for accessibility */}
      <form className="ep-comments__form" onSubmit={handleSubmit}>
        <label htmlFor="comment-name" className="sr-only">Name</label>
        <input
          id="comment-name"
          className="ep-comments__input"
          type="text"
          placeholder="Your name"
          value={name}
          onChange={e => setName(e.target.value)}
          maxLength={80}
          required
        />
        <label htmlFor="comment-text" className="sr-only">Comment</label>
        <textarea
          id="comment-text"
          className="ep-comments__textarea"
          placeholder="Leave a comment..."
          value={text}
          onChange={e => setText(e.target.value)}
          rows={3}
          maxLength={1000}
          required
        />
        <button type="submit" className="ep-comments__submit" style={accentColor ? { background: accentColor, boxShadow: `0 4px 14px color-mix(in srgb, ${accentColor} 40%, transparent)` } : {}}>
          {submitted ? 'Posted!' : 'Post comment'}
        </button>
      </form>

      {comments.length > 0 && (
        <div className="ep-comments__list">
          {comments.map((c, i) => (
            <div key={i} className="ep-comment-card" style={accentColor ? { '--card-accent': accentColor, borderLeft: `3px solid ${accentColor}` } : {}}>
              <div className="ep-comment-card__header">
                <span className="ep-comment-card__name" style={accentColor ? { color: accentColor } : {}}>{c.name}</span>
                <span className="ep-comment-card__time">{formatRelativeTime(c.timestamp)}</span>
              </div>
              <p className="ep-comment-card__text">{c.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Listen Buttons ── */
function ListenButtons({ podcastName, episodeTitle }) {
  const appleUrl = `https://podcasts.apple.com/search?term=${encodeURIComponent((podcastName ?? '') + ' ' + (episodeTitle ?? ''))}`;
  const spotifyUrl = `https://open.spotify.com/search/${encodeURIComponent(episodeTitle ?? '')}`;

  return (
    <div className="ep-listen-buttons">
      <a
        href={appleUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="ep-listen-btn ep-listen-btn--apple"
      >
        <svg className="ep-listen-btn__icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm0 3.5a6.5 6.5 0 110 13 6.5 6.5 0 010-13zm.75 3.25a.75.75 0 00-1.5 0v4.19l-1.72-1.72a.75.75 0 10-1.06 1.06l3 3a.75.75 0 001.06 0l3-3a.75.75 0 10-1.06-1.06l-1.72 1.72V6.75z"/>
        </svg>
        Listen on Apple Podcasts
      </a>
      <a
        href={spotifyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="ep-listen-btn ep-listen-btn--spotify"
      >
        <svg className="ep-listen-btn__icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm4.586 14.424a.622.622 0 01-.857.207c-2.348-1.435-5.304-1.76-8.785-.964a.623.623 0 01-.277-1.215c3.808-.87 7.076-.496 9.712 1.115.294.18.386.565.207.857zm1.223-2.722a.78.78 0 01-1.072.257c-2.687-1.652-6.785-2.131-9.965-1.166a.78.78 0 01-.453-1.492c3.633-1.104 8.149-.569 11.233 1.329a.78.78 0 01.257 1.072zm.105-2.835C12.422 6.975 7.1 6.78 4.205 7.66a.935.935 0 01-.543-1.79c3.324-1.01 8.847-.814 12.34 1.384a.935.935 0 01-.088 1.613z"/>
        </svg>
        Listen on Spotify
      </a>
    </div>
  );
}

export default function EpisodeDetail() {
  // Support both new slug route and legacy UUID route
  const { podcastSlug, episodeSlug, id } = useParams();
  const [searchParams] = useSearchParams();

  // Legacy support: /episode/:id?podcast_uuid=...
  const legacyPodcastId = searchParams.get('podcast_uuid') ?? '';

  // Data the prerenderer inlined for this exact URL. Only the slug route is
  // prerendered; the legacy UUID route depends on a query string and always
  // takes the async path.
  const boot = pageData(`episode:${podcastSlug}:${episodeSlug}`);

  const [episode, setEpisode] = useState(boot?.episode ?? null);
  const [podcast, setPodcast] = useState(boot?.podcast ?? null);
  const [loading, setLoading] = useState(!boot?.episode);
  const [podcastLoading, setPodcastLoading] = useState(!boot?.podcast);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Don't drop back to the skeleton when the inlined payload already answered
    // this URL; re-resolving from data.json would only produce the same record.
    const alreadyResolved = Boolean(episode) && slugify(episode.title) === episodeSlug
      && episode.podcast_uuid === podcastSlug;
    if (alreadyResolved) return () => { cancelled = true; };
    setLoading(true);

    const resolveEpisode = async () => {
      if (podcastSlug && episodeSlug) {
        // New clean URL route
        const [ep, pod] = await Promise.all([
          fetchEpisodeBySlug(podcastSlug, episodeSlug),
          fetchPodcast(podcastSlug),
        ]);
        return { ep, pod };
      } else if (id) {
        // Legacy UUID route
        const [ep, pod] = await Promise.all([
          fetchEpisode(id, legacyPodcastId),
          legacyPodcastId ? fetchPodcast(legacyPodcastId) : Promise.resolve(null),
        ]);
        return { ep, pod };
      }
      return { ep: null, pod: null };
    };

    resolveEpisode()
      .then(({ ep, pod }) => {
        if (!cancelled) {
          setEpisode(ep);
          setPodcast(pod);
          setLoading(false);
          setPodcastLoading(false);
          // S-003: log actual error details to console only
          if (!ep) console.error('Episode not found for', podcastSlug, episodeSlug, id);
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.error('Failed to load episode:', err);
          setError(err.message);
          setLoading(false);
          setPodcastLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [podcastSlug, episodeSlug, id, legacyPodcastId]);

  // Episode description now ships in data.json (captured at ingest time), so it
  // renders instantly — no live RSS fetch, no title-match failure mode.

  // Dynamic meta tags + JSON-LD structured data
  useEffect(() => {
    if (!episode || !podcast) return;
    return applyMeta(episodeMeta(episode, podcast));
  }, [episode, podcast]);

  // FE-019: episode_uuid is the correct field name (confirmed from data.json)
  const episodeUuid = episode?.episode_uuid ?? null;

  const accentColor = getPodcastColor(podcast?.podcast_uuid);
  const description = episode?.description?.trim() || null;
  const isPending = !episode || episode.status !== 'complete' || episode.host_pct === null;

  const isCoHostFormat = !!(podcast?.co_host_name || episode?.co_host_name);
  const firstName = name => name?.split(' ')[0] ?? name;
  // Multi-host episodes (SmartLess): all hosts are merged into "Hosts" vs "Guest".
  const isMultiHost = !!episode?.multi_host;

  // Who is actually in THIS episode. co_host_name is podcast-level, so on a
  // two-host show it claims both hosts and no guest on every episode — which
  // mislabels solo episodes and credits a guest's talk time to the absent
  // co-host. speaker_breakdown is per-episode, so prefer it whenever present
  // and fall back to the old behaviour for episodes not yet backfilled.
  const breakdown  = episode?.speaker_breakdown?.length ? episode.speaker_breakdown : null;
  const bdHosts    = breakdown ? breakdown.filter(s => s.role === 'host') : [];
  const bdGuests   = breakdown ? breakdown.filter(s => s.role === 'guest') : [];
  // Solo: one voice, nobody to compare against, so the comparison stats below
  // are meaningless and get hidden entirely.
  const isSoloEpisode = !!breakdown && bdHosts.length === 1 && bdGuests.length === 0;
  const showQuestions = showsQuestionSplit(episode);

  // Question-count labels. Hosts get named once there is more than one of them;
  // guests stay generic until there is more than one of them. Three or more
  // hosts collapse to "Hosts" because the record only carries two counts.
  const { hostQuestions: hostQ, guestQuestions: guestQ } = questionSplit(episode);
  let hostLabel, guestLabel;
  if (breakdown) {
    if (isSoloEpisode) {
      hostLabel  = firstName(bdHosts[0].name);
      guestLabel = 'Guest';
    } else if (bdHosts.length === 2 && bdGuests.length === 0) {
      hostLabel  = firstName(bdHosts[0].name);
      guestLabel = firstName(bdHosts[1].name);
    } else if (bdHosts.length >= 2 || bdGuests.length >= 2) {
      // Either side holding more than one person breaks the one-to-one keying
      // of the two stored counts, so both sides go generic. "Host / Guest" is
      // only honest on the single-host, single-guest interview.
      hostLabel  = 'Hosts';
      guestLabel = 'Guest';
    } else {
      hostLabel  = 'Host';
      guestLabel = 'Guest';
    }
  } else {
    hostLabel  = isMultiHost ? 'Hosts' : isCoHostFormat ? firstName(podcast?.host_name ?? 'Host') : 'Host';
    guestLabel = isMultiHost ? 'Guest' : isCoHostFormat ? firstName(podcast?.co_host_name || episode?.co_host_name) : 'Guest';
  }

  // Multi-host per-speaker talk-time bar (SmartLess and similar). Only present
  // on episodes ingested with Speaker Identification; others fall back to the
  // classic host/guest bar.
  const speakerBars = episode?.speaker_breakdown?.length
    ? buildSpeakerBars(episode.speaker_breakdown, accentColor, episode.podcast_uuid)
    : null;

  // Back link: prefer podcast slug route, fall back to legacy
  const backTo = podcastSlug
    ? `/podcast/${podcastSlug}`
    : legacyPodcastId
      ? `/podcast/${legacyPodcastId}`
      : '/';

  return (
    <div>
      <nav aria-label="Breadcrumb">
        <Link
          to={backTo}
          className="episode-detail__back back-link"
        >
          {podcast?.name ? `← ${podcast.name}` : '← Back to episode list'}
        </Link>
      </nav>

      {error && (
        <div className="state-box" role="alert" style={{ marginTop: '1rem' }}>
          {/* S-003: show friendly message only; log actual error to console */}
          <p className="state-box__heading">Failed to load episode</p>
          <p className="state-box__body">Failed to load episode. Please try again.</p>
        </div>
      )}

      {/* BE-008: podcast not found after loading */}
      {!loading && !podcastLoading && episode && !podcast && (
        <div className="state-box">
          <p className="state-box__heading">Podcast not found</p>
          <p className="state-box__body">The podcast for this episode could not be found or the link is broken.</p>
        </div>
      )}

      {/* BE-001: episode not found after loading */}
      {!loading && !episode && !error && (
        <div className="state-box">
          <p className="state-box__heading">Episode not found</p>
          <p className="state-box__body">This episode may have been removed or the link is broken.</p>
        </div>
      )}

      {loading ? <DetailSkeleton /> : episode && (
        <>
          {/* ── RPG Header ── */}
          <div className="ep-hero" style={{ '--card-accent': accentColor }}>
            {podcast?.artwork_url && (
              // FE-021: decorative artwork — podcast name shown as adjacent text
              <img
                src={podcast.artwork_url}
                alt=""
                className="ep-hero__art"
              />
            )}
            <div className="ep-hero__body">
              {podcast?.name && (
                <p className="ep-hero__podcast-name" style={{ color: accentColor }}>{podcast.name}</p>
              )}
              <h1 className="ep-hero__title">{episode.title}</h1>
              <div className="ep-hero__meta">
                {episode.published_at && (
                  <span style={{ color: 'var(--color-text-muted)' }}>Published {formatDate(episode.published_at)}</span>
                )}
                {episode.duration_ms != null && (
                  <span>Duration: {formatDuration(episode.duration_ms)}</span>
                )}
              </div>

              {episode.solo_episode && (
                <div className="ep-solo-banner" role="note" aria-label="Solo episode">
                  <span className="ep-solo-banner__icon">🎙</span>
                  <span><strong>Solo episode</strong> — {podcast?.host_name ?? 'The host'} is the only speaker. Stats reflect host monologue only; guest figures are not meaningful.</span>
                </div>
              )}

              {/* Listen buttons */}
              <ListenButtons podcastName={podcast?.name} episodeTitle={episode.title} />

              {/* Share buttons */}
              {!isPending && episode.host_pct != null && (() => {
                // Who the tweet names has to come from this episode, not from
                // the podcast's default host: on a show whose hosts rotate, the
                // default names someone who is not in the episode, and on a
                // two-host episode it credits both hosts' time to one of them.
                // With no guest, the hosts' combined share is 100% by definition
                // and says nothing, so name the one who talked most and quote
                // their own share instead.
                const noGuest = bdHosts.length > 0 && bdGuests.length === 0;
                const shareHosts = noGuest
                  ? [bdHosts.reduce((a, b) => (b.pct > a.pct ? b : a))]
                  : (bdHosts.length ? bdHosts : null);
                const hostName = shareHosts
                  ? shareHosts.map(h => h.name).join(' & ')
                  : (podcast?.host_name ?? 'The host');
                const hostPctVal = (shareHosts && shareHosts.length === 1
                  ? shareHosts[0].pct
                  : episode.host_pct).toFixed(1);
                const shareText = `"${episode.title}" — ${hostName} talked ${hostPctVal}% of the time 🎙 via @TalkMuchOrg`;
                // S-007: build canonical URL from route params, not window.location.href
                const pageUrl = podcastSlug && episodeSlug
                  ? `https://talkmuch.org/podcast/${podcastSlug}/episode/${episodeSlug}`
                  : window.location.href;
                const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(pageUrl)}`;
                function handleCopy() {
                  navigator.clipboard?.writeText(pageUrl).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }).catch(() => {});
                }
                return (
                  <div className="ep-share-row">
                    <a
                      href={tweetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ep-share-btn ep-share-btn--x"
                    >
                      Share on X
                    </a>
                    <button
                      type="button"
                      className="ep-share-btn ep-share-btn--copy"
                      onClick={handleCopy}
                    >
                      {copied ? 'Copied!' : 'Copy link'}
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Episode description (from data.json) */}
          {description && (
            <div className="episode-detail__description" style={{ '--card-accent': accentColor }}>
              <p>{description}</p>
            </div>
          )}

          {/* Star rating */}
          {episodeUuid && <StarRating episodeUuid={episodeUuid} />}

          {/* Speaking breakdown chart */}
          {isPending ? (
            <div className="ep-card ep-card--glow">
              <p className="ep-section-label">Speaking breakdown</p>
              <div className="state-box" style={{ border: 'none', padding: '2rem 0' }}>
                <span className="pending-pill" role="status">Analysis pending</span>
                <p className="state-box__body" style={{ marginTop: '0.5rem' }}>
                  Transcript analysis is in progress — check back soon.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Zone A — Speaking stats */}
              <div className="ep-card ep-card--glow" style={{ '--card-accent': accentColor }}>
                <p className="ep-section-label">Speaking breakdown</p>
                {speakerBars
                  ? <SpeakingBar speakers={speakerBars} />
                  : <SpeakingBar hostPct={episode.host_pct} guestPct={episode.guest_pct} hostLabel={hostLabel} guestLabel={guestLabel} accentColor={accentColor} />}

                {/* Zone B — Meta stats row (EpisodeStats pill rows). Hidden on a
                    solo episode: turn switches, monologue length and the
                    host-vs-guest question split all compare two speakers, and
                    there is only one. */}
                {!isSoloEpisode && (episode.turn_switches != null || hostQ != null) && (
                  <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--color-border)', paddingTop: '1rem' }}>
                    <EpisodeStats
                      turnSwitches={episode.turn_switches}
                      avgTurnMs={episode.avg_turn_ms}
                      longestMonologueMs={episode.longest_monologue_ms}
                      hostQuestions={hostQ}
                      guestQuestions={guestQ}
                      hostLabel={hostLabel}
                      guestLabel={guestLabel}
                      showQuestions={showQuestions}
                    />
                  </div>
                )}

              </div>

              {/* Zone C — Top words from the transcript */}
              {episode.top_words?.length > 0 && (
                <div className="ep-card" style={{ '--card-accent': accentColor }}>
                  <p className="ep-section-label">Top words</p>
                  <div className="ep-top-words">
                    {episode.top_words.slice(0, 5).map(w => (
                      <span key={w.word} className="ep-top-word">
                        {w.word}
                        {w.count != null && (
                          <span className="ep-top-word__count">{w.count}</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

            </>
          )}

          {/* Comments */}
          {episodeUuid && <Comments episodeUuid={episodeUuid} accentColor={accentColor} />}
        </>
      )}
    </div>
  );
}
